import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, MapPin, ExternalLink } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import gepositLogo from "@/assets/geposit-logo.png";

export function Footer() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  return (
    <footer className="bg-primary text-primary-foreground min-h-[400px]" role="contentinfo">
      <div className="container mx-auto px-4 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img src={gepositLogo} alt="Geposit – hem" width={243} height={40} className="h-8 w-auto brightness-0 invert mix-blend-screen" loading="lazy" style={{ aspectRatio: '481/80' }} />
            <p className="mt-4 text-sm text-primary-foreground/80 font-body leading-relaxed">
              {t("footer.description")}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <p className="font-subheading font-bold text-sm uppercase tracking-wider mb-4 text-primary-foreground/90">
              {t("footer.quickLinks")}
            </p>
            <ul className="space-y-1">
              {[
                { label: t("nav.losningar"), href: "/losningar" },
                { label: t("nav.produkter"), href: "/tjanster" },
                { label: t("nav.nyheter"), href: "/information/nyheter" },
                { label: t("nav.villkor"), href: "/information/villkor" },
                { label: t("nav.omGeposit"), href: "/om-geposit" },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={getLocalizedPath(link.href)}
                    className="text-sm text-primary-foreground/80 hover:text-coral transition-colors inline-flex items-center min-h-[44px]"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="font-subheading font-bold text-sm uppercase tracking-wider mb-4 text-primary-foreground/90">
              {t("footer.contact")}
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5 text-sm text-primary-foreground/80">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-coral" />
                {t("footer.address")}
              </li>
              <li>
                <a
                  href={`mailto:${t("footer.email")}`}
                  className="flex items-center gap-2.5 text-sm text-primary-foreground/80 hover:text-coral transition-colors"
                >
                  <Mail className="h-4 w-4 shrink-0 text-coral" />
                  {t("footer.email")}
                </a>
              </li>
            </ul>
          </div>

          {/* About / Partners */}
          <div>
            <p className="font-subheading font-bold text-sm uppercase tracking-wider mb-4 text-primary-foreground/90">
              {t("footer.partOf")}
            </p>
            <p className="text-sm text-primary-foreground/80 font-body leading-relaxed mb-2">
              {t("footer.progritsDesc")}
            </p>
            <a
              href="https://www.progrits.se"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-xs text-coral hover:text-coral/80 transition-colors mb-5 min-h-[44px]"
              aria-label="Besök progrits.se (öppnas i nytt fönster)"
            >
              progrits.se
              <ExternalLink className="ml-1 h-3 w-3" aria-hidden="true" />
            </a>

            <p className="font-subheading font-bold text-sm uppercase tracking-wider mb-2 text-primary-foreground/90">
              {t("footer.sister")}
            </p>
            <p className="text-sm text-primary-foreground/80 font-body leading-relaxed mb-2">
              <span className="font-semibold text-primary-foreground/90">{t("footer.postnummerservice")}</span> – {t("footer.postnummerserviceDesc")}
            </p>
            <a
              href="https://www.postnummerservice.se"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-xs text-coral hover:text-coral/80 transition-colors mb-5 min-h-[44px]"
              aria-label="Besök postnummerservice.se (öppnas i nytt fönster)"
            >
              postnummerservice.se
              <ExternalLink className="ml-1 h-3 w-3" aria-hidden="true" />
            </a>

            <p className="text-sm text-primary-foreground/80 font-body leading-relaxed mb-2">
              <span className="font-semibold text-primary-foreground/90">{t("footer.ecoroute")}</span> – {t("footer.ecorouteDesc")}
            </p>
            <a
              href="https://www.ecoroute.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-xs text-coral hover:text-coral/80 transition-colors min-h-[44px]"
              aria-label="Besök ecoroute.com (öppnas i nytt fönster)"
            >
              ecoroute.com
              <ExternalLink className="ml-1 h-3 w-3" aria-hidden="true" />
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-primary-foreground/10 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-primary-foreground/70">
            {t("footer.copyright")}
          </p>
          <div className="flex gap-6">
            <Link
              to={getLocalizedPath("/integritetspolicy")}
              className="text-xs text-primary-foreground/70 hover:text-coral transition-colors inline-flex items-center min-h-[44px]"
            >
              {t("footer.privacy")}
            </Link>
            <Link
              to={getLocalizedPath("/information/villkor")}
              className="text-xs text-primary-foreground/70 hover:text-coral transition-colors inline-flex items-center min-h-[44px]"
            >
              {t("footer.terms")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
